import { Skeleton } from 'components/Skeleton'
import { Sparkles } from 'lucide-react'
import { BorderTravelOverlay } from '../previews/ShimmerOverlay'
import type { LivePlatform } from '../store/idea-store'

/**
 * Per-platform aspect ratio for the asset slot. Picked to match the slot
 * the real preview component renders, so the swap from skeleton → real
 * preview doesn't shift layout. See `InstagramPostPreview.tsx:167` (square),
 * `PinterestPinPreview.tsx:134` (2:3), TikTok / YouTube Shorts (9:16
 * vertical), etc.
 */
const ASSET_ASPECT: Record<LivePlatform, string> = {
  instagram: 'aspect-square',
  facebook: 'aspect-square',
  linkedin: 'aspect-square',
  pinterest: 'aspect-[2/3]',
  tiktok: 'aspect-[9/16]',
  youtube_shorts: 'aspect-[9/16]',
  // XPostPreview's own image skeleton is `aspect-video`.
  x: 'aspect-video',
}

/**
 * Vertical formats are narrower than feed cards so the asset doesn't
 * dominate the modal canvas — mirrors how phone-shaped previews render
 * elsewhere in the app.
 */
const MAX_WIDTH: Record<LivePlatform, string> = {
  instagram: 'max-w-[420px]',
  facebook: 'max-w-[420px]',
  linkedin: 'max-w-[420px]',
  pinterest: 'max-w-[300px]',
  tiktok: 'max-w-[300px]',
  youtube_shorts: 'max-w-[300px]',
  x: 'max-w-[420px]',
}

interface PostModalPreviewSkeletonProps {
  platform: LivePlatform
}

/**
 * Loading state for `PostModalPreviewPane` while a sibling creative is being
 * reframed + populated by the agent. Mirrors the chrome of the real preview
 * (header avatar + asset slot + caption lines) so the swap to live content
 * doesn't pop in.
 *
 * One `<BorderTravelOverlay />` covers the whole card — same recipe as
 * `IdeaCard`. T3-native (bg-bg-2 / bg-bg-3 / border-line); intentionally not
 * reusing the legacy light-chrome skeletons in `previews/*SkeletonPreview.tsx`
 * which render with `bg-white` + the deprecated purple ring.
 */
export function PostModalPreviewSkeleton({ platform }: PostModalPreviewSkeletonProps) {
  const assetAspect = ASSET_ASPECT[platform]
  const maxWidth = MAX_WIDTH[platform]

  return (
    <div
      className={`relative w-full border-line bg-bg-2 ${maxWidth} overflow-hidden rounded-[12px] border`}
    >
      <BorderTravelOverlay />

      <div className="flex items-center gap-3 px-3 py-3">
        <Skeleton variant="dark" className="h-8 w-8 shrink-0 !rounded-full" />
        <div className="flex flex-col gap-1.5">
          <Skeleton variant="dark" className="h-3 w-24" />
          <Skeleton variant="dark" className="h-2.5 w-16" />
        </div>
      </div>

      {/* The imagery slot speaks the Home hero's frame language: the same
          dashed frame Taka asked for shots with, now being filled — acid
          light sweep + twinkling sparkle while the post is placed into it. */}
      <div className={`relative w-full bg-bg-3 ${assetAspect}`}>
        <div className="absolute inset-6 flex items-center justify-center overflow-hidden rounded-[10px] border border-dashed border-acid/25 bg-acid/[0.03]">
          <span className="hero-frame-sweep absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-acid/10 to-transparent" />
          <Sparkles size={18} className="hero-sparkle text-acid/60" />
        </div>
      </div>

      <div className="flex flex-col gap-2 px-3 py-3">
        <Skeleton variant="dark" className="h-3 w-[70%]" />
        <Skeleton variant="dark" className="h-3 w-[85%]" />
        <Skeleton variant="dark" className="h-3 w-[40%]" />
      </div>
    </div>
  )
}
